const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:9000";

export const API_CONFIG = {
  baseUrl: API_BASE_URL,
  timeout: 30000,
  defaultPageSize: 20,
  maxPageSize: 500,
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  login: `${API_BASE_URL}/api/auth/login`,
  logout: `${API_BASE_URL}/api/auth/logout`,
  refresh: `${API_BASE_URL}/api/auth/refresh`,
  signup: `${API_BASE_URL}/api/auth/signup`,
  userInfo: `${API_BASE_URL}/api/auth/me`,

  // Validation (unified API at /api/validation)
  validationIssues: `${API_BASE_URL}/api/validation/issues`,
  validationIssue: (issueId: number) =>
    `${API_BASE_URL}/api/validation/issues/${issueId}`,
  validationIssueResolve: (issueId: number) =>
    `${API_BASE_URL}/api/validation/issues/${issueId}/resolve`,
  validationRunsStats: (runId: number) =>
    `${API_BASE_URL}/api/validation/runs/${runId}/stats`,
  validationProductIssues: (productId: string) =>
    `${API_BASE_URL}/api/validation/products/${productId}/issues`,
  validationVariantIssues: (variantId: string) =>
    `${API_BASE_URL}/api/validation/variants/${variantId}/issues`,

  /** Agent prompt for running validation for a specific product/variant (query: product_id, variant_id) */
  validationAgentPrompt: `${API_BASE_URL}/api/validation/agent/prompt`,

  // Reviews (review status tracking)
  reviewsBase: `${API_BASE_URL}/api/reviews`,
  reviewsQueue: `${API_BASE_URL}/api/reviews/queue`,
  reviewsQueueGrouped: `${API_BASE_URL}/api/reviews/queue/grouped`,
  reviewsQueueCount: `${API_BASE_URL}/api/reviews/queue/count`,
  /** Stale review groups for a source (optional ?entity_type=product|variant). */
  reviewStaleGroups: (sourceId: string) =>
    `${API_BASE_URL}/api/reviews/sources/${encodeURIComponent(sourceId)}/stale-groups`,
  /** Bulk-resolve stale reviews for a source by entity_type + changed_fields. */
  reviewBulkResolve: (sourceId: string) =>
    `${API_BASE_URL}/api/reviews/sources/${encodeURIComponent(sourceId)}/bulk-resolve`,
  reviewMatrix: (sourceId: string) =>
    `${API_BASE_URL}/api/reviews/sources/${encodeURIComponent(sourceId)}/review-groups/product-grade-diagonal`,
  reviewMatrixResolve: (sourceId: string) =>
    `${API_BASE_URL}/api/reviews/sources/${encodeURIComponent(sourceId)}/review-groups/product-grade-diagonal/resolve`,
  reviewVariantGroupStatus: (variantId: number) =>
    `${API_BASE_URL}/api/reviews/variants/${variantId}/review-groups/single-product-variants`,
  /** POST with SetReviewStatusRequest body (status: unreviewed | in_progress | stale | reviewed | canceled). */
  reviewProductStatus: (productId: number) =>
    `${API_BASE_URL}/api/reviews/products/${productId}/status`,
  reviewVariantStatus: (variantId: number) =>
    `${API_BASE_URL}/api/reviews/variants/${variantId}/status`,
  reviewProductChanges: (productId: number) =>
    `${API_BASE_URL}/api/reviews/products/${productId}/changes`,
  reviewVariantChanges: (variantId: number) =>
    `${API_BASE_URL}/api/reviews/variants/${variantId}/changes`,
  reviewProductOverview: (productId: number) =>
    `${API_BASE_URL}/api/reviews/products/${productId}/overview`,

  // Products
  product: (productId: number) => `${API_BASE_URL}/api/products/${productId}`,
  /** Product by variant ID (GET /api/products/by-variant/{variant_id}). */
  productByVariant: (variantId: number) =>
    `${API_BASE_URL}/api/products/by-variant/${variantId}`,
  /** Search products by name (?query=&source=&available_only=&limit=). */
  productsSearchName: `${API_BASE_URL}/api/products/search/name`,
  /** Search products by URL (?query=&limit=). */
  productsSearchUrl: `${API_BASE_URL}/api/products/search/url`,

  // Featured Products
  /** List/create featured products (?context=&active_only=). */
  featuredProducts: `${API_BASE_URL}/api/featured-products/`,
  /** Get/update/delete a specific featured product by ID. */
  featuredProduct: (id: number) =>
    `${API_BASE_URL}/api/featured-products/${id}`,
  /** Bulk reorder featured products. */
  featuredProductsReorder: `${API_BASE_URL}/api/featured-products/reorder`,
  /** All featured entries for a product. */
  featuredProductsForProduct: (productId: number) =>
    `${API_BASE_URL}/api/featured-products/product/${productId}`,

  // Scraping (still under /api/scrapers)
  scrape: `${API_BASE_URL}/api/scrapers/scrape`,
  scrapeUrl: `${API_BASE_URL}/api/scrapers/scrape/url`,
  scrapeProduct: (productId: number) =>
    `${API_BASE_URL}/api/scrapers/scrape/product/${productId}`,
  scrapersRuns: `${API_BASE_URL}/api/scrapers/runs`,
  scrapersRunLogs: (runId: number) =>
    `${API_BASE_URL}/api/scrapers/runs/${runId}/logs`,
  scrapersSources: `${API_BASE_URL}/api/scrapers/sources`,
  scrapersLogosActive: `${API_BASE_URL}/api/scrapers/logos/active`,
  scrapersLogo: (sourceId: string) =>
    `${API_BASE_URL}/api/scrapers/logos/${encodeURIComponent(sourceId)}`,
  scrapersLogoUpload: (sourceId: string) =>
    `${API_BASE_URL}/api/scrapers/logos/${encodeURIComponent(sourceId)}/upload`,
  scrapersSitemapHealth: `${API_BASE_URL}/api/scrapers/sitemap-health`,
  scrapersSourceSitemapHealth: (sourceId: string) =>
    `${API_BASE_URL}/api/scrapers/sitemap-health/${encodeURIComponent(sourceId)}`,
  /** Rendered scraped HTML (text/html) for product page. */
  scrapersProductView: (productId: number) =>
    `${API_BASE_URL}/api/scrapers/html/product/${productId}/view`,
  /** Rendered scraped HTML (text/html) for variant page. */
  scrapersVariantView: (variantId: number) =>
    `${API_BASE_URL}/api/scrapers/html/variant/${variantId}/view`,

  // Jobs (polling for async scraper execution; list/delete for admin)
  jobsList: `${API_BASE_URL}/api/jobs`,
  job: (jobId: string) => `${API_BASE_URL}/api/jobs/${jobId}`,

  // Admin runtime / diagnostics
  adminEnvironment: `${API_BASE_URL}/api/admin/environment`,
  adminCronSchedule: `${API_BASE_URL}/api/admin/cron-schedule`,
  adminCapabilities: `${API_BASE_URL}/api/admin/capabilities`,
  adminCapability: (overrideId: number) =>
    `${API_BASE_URL}/api/admin/capabilities/${overrideId}`,
  adminCapabilityRegistry: `${API_BASE_URL}/api/admin/capabilities/registry`,
  adminCapabilityTemplates: `${API_BASE_URL}/api/admin/capabilities/templates`,
  adminCapabilitiesBulk: `${API_BASE_URL}/api/admin/capabilities/bulk`,
  adminCapabilityExplain: (capability: string) =>
    `${API_BASE_URL}/api/admin/capabilities/${encodeURIComponent(capability)}/explain`,
  adminCapabilityOrphans: `${API_BASE_URL}/api/admin/capabilities/orphans`,
  adminCapabilityExpired: `${API_BASE_URL}/api/admin/capabilities/expired`,
  adminCapabilityPurgeExpired: `${API_BASE_URL}/api/admin/capabilities/purge-expired`,

  // Admin users & subscriptions
  adminUsers: `${API_BASE_URL}/api/admin/users`,
  adminUser: (userId: number) => `${API_BASE_URL}/api/admin/users/${userId}`,
  adminUserSubscription: (userId: number) =>
    `${API_BASE_URL}/api/admin/users/${userId}/subscription`,
  adminUserDeletions: `${API_BASE_URL}/api/admin/users/deletions`,
  adminSubscriptionMetrics: `${API_BASE_URL}/api/admin/subscriptions/metrics`,

  // Admin business accounts
  businessAccounts: `${API_BASE_URL}/api/admin/business-accounts`,
  businessAccount: (id: number) =>
    `${API_BASE_URL}/api/admin/business-accounts/${id}`,
  businessAccountResendInvite: (id: number) =>
    `${API_BASE_URL}/api/admin/business-accounts/${id}/resend-invite`,

  // Admin newsletter
  newsletterSubmissions: `${API_BASE_URL}/api/admin/newsletter/submissions`,
  newsletterSubmission: (submissionId: number) =>
    `${API_BASE_URL}/api/admin/newsletter/submissions/${submissionId}`,
  newsletterIssues: `${API_BASE_URL}/api/admin/newsletter/issues`,
  newsletterIssue: (issueId: number) =>
    `${API_BASE_URL}/api/admin/newsletter/issues/${issueId}`,
  newsletterIssueRender: (issueId: number) =>
    `${API_BASE_URL}/api/admin/newsletter/issues/${issueId}/render`,
  newsletterIssueTestSend: (issueId: number) =>
    `${API_BASE_URL}/api/admin/newsletter/issues/${issueId}/test-send`,
  newsletterIssueSend: (issueId: number) =>
    `${API_BASE_URL}/api/admin/newsletter/issues/${issueId}/send`,

  // Admin Email Testing
  emailTestConfig: `${API_BASE_URL}/api/admin/email/config`,
  emailTestSend: `${API_BASE_URL}/api/admin/email/test-send`,
  emailTestHistory: `${API_BASE_URL}/api/admin/email/test-history`,

  // Featured Sources (sponsorships)
  featuredSources: `${API_BASE_URL}/api/featured-sources/`,
  featuredSourceById: (id: number) =>
    `${API_BASE_URL}/api/featured-sources/${id}`,

  // Coupons
  coupons: `${API_BASE_URL}/api/coupons/`,
  couponById: (id: number) => `${API_BASE_URL}/api/coupons/${id}`,

  // Sources
  sources: `${API_BASE_URL}/api/sources/`,
  sourcesAdminList: `${API_BASE_URL}/api/sources/admin/list`,
  source: (sourceId: string) =>
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}`,
  sourceDetails: (sourceId: string) =>
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}/details`,
  sourcesCount: `${API_BASE_URL}/api/sources/count`,
  sourcesMapping: `${API_BASE_URL}/api/sources/mapping`,

  // External reviews (admin listing + ingest)
  externalReviews: `${API_BASE_URL}/api/sources/admin/external-reviews`,
  externalReviewsCount: `${API_BASE_URL}/api/sources/admin/external-reviews/count`,
  externalReview: (reviewId: number) =>
    `${API_BASE_URL}/api/sources/admin/external-reviews/${reviewId}`,
  externalReviewSentiment: (reviewId: number) =>
    `${API_BASE_URL}/api/sources/admin/external-reviews/${reviewId}/sentiment`,
  sourceExternalReviews: (sourceId: string) =>
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}/external-reviews`,
  sourceExternalReviewsBulk: (sourceId: string) =>
    `${API_BASE_URL}/api/sources/${encodeURIComponent(sourceId)}/external-reviews/bulk`,

  // NVG Simulation
  nvgPresets: `${API_BASE_URL}/api/nvg-simulation/presets`,
  nvgBake: `${API_BASE_URL}/api/nvg-simulation/simulate/bake`,
  nvgBakePreset: (name: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/bake/preset/${name}`,
  nvgBakeStatus: (jobId: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/bake/${jobId}`,
  nvgBakeAsset: (jobId: string, assetName: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/bake/${jobId}/asset/${assetName}`,
  nvgRegressionTestCases: `${API_BASE_URL}/api/nvg-simulation/regression/test-cases`,
  nvgRegressionBake: (name: string) =>
    `${API_BASE_URL}/api/nvg-simulation/regression/test-cases/${name}/bake`,
  nvgRegressionBaseline: (name: string, type: string) =>
    `${API_BASE_URL}/api/nvg-simulation/regression/test-cases/${name}/baseline/${type}`,

  // NVG Catalog maintenance (admin-only). Separate from the delivery routes
  // above: those serve images a subscriber's tube already has, whereas these
  // measure the catalog's state and start renders against it.
  nvgCatalogPending: `${API_BASE_URL}/api/nvg-catalog/pending`,
  nvgCatalogStale: (refresh: boolean) =>
    `${API_BASE_URL}/api/nvg-catalog/stale${refresh ? "?refresh=true" : ""}`,
  // The orphan sweep's dry-run plan. Fetching this is a precondition for
  // starting a sweep, not merely a readout: the server refuses the apply
  // unless the client echoes back the `to_reap` this returned.
  nvgCatalogSweep: (refresh: boolean) =>
    `${API_BASE_URL}/api/nvg-catalog/sweep${refresh ? "?refresh=true" : ""}`,
  nvgCatalogRuns: `${API_BASE_URL}/api/nvg-catalog/runs`,
  // `run_id` is an SSM command id for a delta or sweep run and an ECS task
  // ARN for a bulk one. Deliberately *not* encoded: the route is declared
  // `{run_id:path}` precisely so an ARN's slashes pass through as path
  // segments, and percent-encoding them would leave the proxy and the router
  // disagreeing about where the parameter ends.
  nvgCatalogRun: (runId: string, kind: string) =>
    `${API_BASE_URL}/api/nvg-catalog/runs/${runId}?kind=${kind}`,

  // Site Gate
  siteGate: `${API_BASE_URL}/api/site-gate/admin`,
  siteGateUpdate: `${API_BASE_URL}/api/site-gate`,

  // Variant Overrides
  variantOverrides: `${API_BASE_URL}/api/variant-overrides/`,
  variantOverride: (id: number) =>
    `${API_BASE_URL}/api/variant-overrides/${id}`,
  variantOverrideApply: (id: number) =>
    `${API_BASE_URL}/api/variant-overrides/${id}/apply`,

  // NVG Contextual Scene Simulation
  nvgContextualPresets: `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/presets`,
  nvgContextualPreset: (presetName: string, tubePreset: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/preset/${presetName}?tube_preset=${tubePreset}`,
  nvgContextualStatus: (jobId: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/${jobId}`,
  nvgContextualAsset: (jobId: string, assetName: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/${jobId}/asset/${assetName}`,
  nvgContextualReveal: (presetName: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/preset/${presetName}/reveal`,
  nvgContextualNightContext: (presetName: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/preset/${presetName}/naked-eye-context`,
  nvgContextualNightMagnified: (presetName: string) =>
    `${API_BASE_URL}/api/nvg-simulation/simulate/contextual/preset/${presetName}/naked-eye-magnified`,
} as const;
